import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const statusBadge = (s) => {
  const map = { pending: "warning", in_review: "info", approved: "success", rejected: "danger", cancelled: "secondary" };
  return <span className={`badge bg-${map[s] || "secondary"} text-capitalize`}>{(s || "").replace("_", " ")}</span>;
};

const entityLabel = (row) => {
  const map = { journal_entry: "Journal Entry", voucher: "Voucher", invoice: "Invoice", payroll_run: "Payroll Run" };
  return `${map[row.entity_type] || row.entity_type} #${row.entity_id}`;
};

const LedgerApprovals = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [p, a, m] = await Promise.all([
      ajaxAccounting.pendingApprovals({}),
      ajaxAccounting.listApprovals({ page: 1 }),
      ajaxAccounting.myApprovalSubmissions({ page: 1 }),
    ]);
    if (p.status === "OK") setPending(Array.isArray(p.details) ? p.details : p.details?.list || []);
    if (a.status === "OK") setAll(a.details?.list || []);
    if (m.status === "OK") setMine(m.details?.list || []);
    setLoading(false);
  };

  const openDetail = async (row) => {
    const res = await ajaxAccounting.getApprovalRequest({ request_id: row.id });
    if (res.status === "OK") setDetail(res.details);
    else toast.error(res.message || "Could not load request");
  };

  const doApprove = async (row) => {
    setActioning(true);
    const res = await ajaxAccounting.approveRequest({ request_id: row.id });
    if (res.status === "OK") { toast.success(res.message || "Approved"); setDetail(null); fetchAll(); }
    else toast.error(res.message || "Approval failed");
    setActioning(false);
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("A reason is required to reject."); return; }
    setActioning(true);
    const res = await ajaxAccounting.rejectRequest({ request_id: rejectFor.id, reason: rejectReason });
    if (res.status === "OK") { toast.success(res.message || "Rejected"); setRejectFor(null); setRejectReason(""); setDetail(null); fetchAll(); }
    else toast.error(res.message || "Rejection failed");
    setActioning(false);
  };

  const baseCols = [
    { key: "request_number", label: "Request #", render: (v, row) => <code className="fw-bold">{v || `REQ-${row.id}`}</code> },
    { key: "entity_type", label: "Entity", render: (v, row) => entityLabel(row) },
    { key: "amount", label: "Amount", render: (v) => <span className="fw-semibold">{functions.formatCurrency(v)}</span> },
    { key: "status", label: "Status", render: (v) => statusBadge(v) },
    { key: "submitted_at", label: "Submitted", render: (v) => <span className="tx-12">{v ? functions.formatDate(v) : "-"}</span> },
  ];

  const pendingCols = [
    ...baseCols,
    {
      key: "id", label: "Action",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" title="Details" onClick={() => openDetail(row)}><i className="fa-solid fa-circle-info"></i></button>
          <button className="btn btn-sm btn-success" title="Approve" onClick={() => doApprove(row)} disabled={actioning}><i className="fa-solid fa-check"></i></button>
          <button className="btn btn-sm btn-danger" title="Reject" onClick={() => { setRejectFor(row); setRejectReason(""); }}><i className="fa-solid fa-xmark"></i></button>
        </div>
      ),
    },
  ];

  const viewCols = [
    ...baseCols,
    { key: "id", label: "", render: (id, row) => <button className="btn btn-sm btn-light" title="Details" onClick={() => openDetail(row)}><i className="fa-solid fa-circle-info"></i></button> },
  ];

  const data = tab === "pending" ? pending : tab === "all" ? all : mine;
  const cols = tab === "pending" ? pendingCols : viewCols;

  return (
    <AppContainer
      title="Ledger Approvals"
      previousPageTitle="Accounting"
      description="Review and action ledger entries submitted for approval. Approvals move through the configured workflow levels; the final approval posts the entry to the ledger."
      actions={[{ label: "Workflow Setup", icon: "fa-solid fa-diagram-project", className: "btn btn-outline-secondary", onClick: () => navigate("/accounting/approvals/workflows") }]}
    >
      <Toaster />
      <ul className="nav nav-tabs mb-3">
        {[
          { key: "pending", label: `Awaiting My Approval (${pending.length})` },
          { key: "all", label: "All Requests" },
          { key: "mine", label: "My Submissions" },
        ].map((t) => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>{t.label}</button>
          </li>
        ))}
      </ul>

      <div className="card custom-card p-3">
        <DataTable columns={cols} data={data} loading={loading} pageSize={15}
          emptyMessage={tab === "pending" ? "Nothing awaiting your approval." : tab === "mine" ? "You have not submitted anything for approval." : "No approval requests."} />
      </div>

      {/* Detail modal */}
      {detail && (
        <AppModal
          title={<>{detail.request_number || `REQ-${detail.id}`} — {entityLabel(detail)}</>}
          size="lg"
          onClose={() => setDetail(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setDetail(null)}>Close</button>
              {detail.status === "pending" || detail.status === "in_review" ? (
                <>
                  <button className="btn btn-danger" onClick={() => { setRejectFor(detail); setRejectReason(""); }}>Reject</button>
                  <button className="btn btn-success" onClick={() => doApprove(detail)} disabled={actioning}>{actioning ? "Working..." : "Approve"}</button>
                </>
              ) : null}
            </>
          }
        >
          <div className="row g-2 mb-3">
            <div className="col-lg-3"><div className="tx-11 tx-muted">Amount</div><div className="fw-semibold">{functions.formatCurrency(detail.amount)}</div></div>
            <div className="col-lg-3"><div className="tx-11 tx-muted">Status</div>{statusBadge(detail.status)}</div>
            <div className="col-lg-3"><div className="tx-11 tx-muted">Current Level</div><div>{detail.current_level ?? "-"}</div></div>
            <div className="col-lg-3"><div className="tx-11 tx-muted">Submitted</div><div className="tx-12">{detail.submitted_at ? functions.formatDate(detail.submitted_at) : "-"}</div></div>
            {detail.notes && <div className="col-12"><div className="tx-11 tx-muted">Notes</div><div className="tx-13">{detail.notes}</div></div>}
          </div>
          <div className="fw-semibold tx-12 tx-uppercase tx-muted mb-2">Approval Steps</div>
          <table className="table table-sm table-bordered">
            <thead><tr><th>Level</th><th>Approver</th><th>Status</th><th>Comments</th></tr></thead>
            <tbody>
              {(detail.steps || []).map((s, i) => (
                <tr key={i}>
                  <td>{s.level_order}</td>
                  <td className="tx-12">{s.label || `${s.approver_type} #${s.approver_id}`}</td>
                  <td>{statusBadge(s.status)}</td>
                  <td className="tx-12">{s.comments || "-"}</td>
                </tr>
              ))}
              {(!detail.steps || detail.steps.length === 0) && <tr><td colSpan={4} className="tx-muted text-center">No steps.</td></tr>}
            </tbody>
          </table>
        </AppModal>
      )}

      {/* Reject modal */}
      {rejectFor && (
        <AppModal
          title={<>Reject {rejectFor.request_number || `REQ-${rejectFor.id}`}</>}
          onClose={() => setRejectFor(null)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setRejectFor(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={doReject} disabled={actioning}>{actioning ? "Working..." : "Confirm Reject"}</button>
            </>
          }
        >
          <label className="form-label tx-12 fw-semibold">Reason <span className="tx-danger">*</span></label>
          <textarea className="form-control" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Explain why this is being rejected..." />
        </AppModal>
      )}
    </AppContainer>
  );
};

export default LedgerApprovals;
